import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import type { TrainingAttemptSummary, TrainingStatus } from '@hcc/types';

export interface TrainingProgressCell {
  task_id: string;
  status: TrainingStatus;
  attempts: number;
  best_score: number | null;
  passed_at: string | null;
  certificate: boolean;
  /** Self-enrolled extra rather than required (not counted in the roll-up). */
  optional: boolean;
}

export interface TrainingProgressRow {
  person: { id: string; full_name: string; email: string; position: string | null };
  cells: Record<string, TrainingProgressCell>;
  /** Roll-up across this person's assigned modules. */
  summary: { assigned: number; passed: number };
}

export interface TrainingProgressView {
  /** graded=false is an unmarked assessment: cells show completion, not a score. */
  /** external=true is a redirect module: staff may mark it complete on the learner's behalf. */
  modules: { id: string; title: string; graded: boolean; external: boolean }[];
  people: TrainingProgressRow[];
}

export interface TrainingProgressFilter {
  module_id?: string;
  mobilisation_id?: string;
  status?: TrainingStatus;
}

export function progressQueryString(filter: TrainingProgressFilter): string {
  const params = new URLSearchParams();
  if (filter.module_id) params.set('module_id', filter.module_id);
  if (filter.mobilisation_id) params.set('mobilisation_id', filter.mobilisation_id);
  if (filter.status) params.set('status', filter.status);
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

export function useTrainingProgress(filter: TrainingProgressFilter) {
  return useQuery({
    queryKey: ['admin', 'training-progress', filter] as const,
    queryFn: async () =>
      api<TrainingProgressView>(`/api/admin/training-progress${progressQueryString(filter)}`),
  });
}

/** Attempt history for one matrix cell; fetched only while its modal is open. */
export function useTrainingAttemptHistory(personId: string | null, taskId: string | null) {
  return useQuery({
    queryKey: ['admin', 'training-progress', 'attempts', personId, taskId] as const,
    enabled: !!personId && !!taskId,
    queryFn: async () =>
      api<{ person: { id: string; full_name: string }; attempts: TrainingAttemptSummary[] }>(
        `/api/admin/training-progress/${personId}/${taskId}/attempts`,
      ),
  });
}

/**
 * Staff mark an external module complete for a learner who finished it
 * off-platform but never self-certified. External modules only (the server
 * refuses quizzes). Invalidates the matrix so the cell flips to passed.
 */
export function useStaffCompleteTraining() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { personId: string; taskId: string; reason?: string }) =>
      api<{ completed: true }>(
        `/api/admin/training-progress/${input.personId}/${input.taskId}/complete`,
        { method: 'POST', body: JSON.stringify({ reason: input.reason || undefined }) },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'training-progress'] });
    },
  });
}

/**
 * HR reset of one person's progress on one module: clears their saved
 * draft, voids that module's attempts and re-assigns it, so the next open
 * starts fresh on the current published version. Invalidates the matrix
 * and the cell's attempt history.
 */
export function useResetTrainingProgress() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { personId: string; taskId: string; reason?: string }) =>
      api<{ reset: true; voided_attempts: number; cleared_draft: boolean }>(
        `/api/admin/training-progress/${input.personId}/${input.taskId}/reset`,
        { method: 'POST', body: JSON.stringify({ reason: input.reason || undefined }) },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'training-progress'] });
    },
  });
}
